// tslint:disable:max-classes-per-file

import React from "react";
import { EnterHook, RouteProps } from "react-router";
import _ from "underscore";
import { isServer } from "../../core/detection";
import { ErrorBoundary } from "../components/errorBoundary";
import { CustomErrors } from "../errors";

/**
 * Enter hook to require auth on a certain route.
 */
export function requireAuth(
  allowedUserType: UserType,
  store: Redux.Store<IWebStoreState>,
  loginRoute: string,
  onEnter?: EnterHook,
): EnterHook {
  return (nextState, replaceState, callback) => {
    if (isServer()) {
      return callback(new CustomErrors.CannotRenderOnServerError("can't do auth server-side"));
    }

    const storeState = store.getState();
    if (!storeState.user.data) {
      replaceState({
        pathname: loginRoute,
        state: { nextPathname: nextState.location.pathname },
      });
      callback();
      return;
    }

    if (storeState.user.data.userType !== allowedUserType) {
      replaceState({ pathname: "/404" });
      callback();
      return;
    }

    if (onEnter) {
      onEnter(nextState, replaceState, callback);
      return;
    }

    callback();
  };
}

const isElement = (x: any): x is React.ReactElement<any> => !!x.props;

/**
 * Traverse and transform a tree of elements.
 */
const visitElements = (element: JSX.Element, mapProps: (oldProps: any) => any): JSX.Element => {
  const newChildren = React.Children.map(element.props.children, child => {
    if (isElement(child)) {
      return visitElements(child, mapProps);
    }

    return child;
  });

  return React.cloneElement(element, _.omit(mapProps(element.props), "key", "ref"), newChildren);
};

/**
 * Wrap a route's component in an error boundary.
 */
const addErrorBoundary = (props: RouteProps) => {
  // Not all routes have a component
  if (!props.component) {
    return props;
  }

  const component = class extends React.Component {
    public render() {
      const WrappedComponent = props.component;
      return (
        <ErrorBoundary>
          <WrappedComponent {...this.props} />
        </ErrorBoundary>
      );
    }
  };

  return { ...props, component };
};

/**
 * Wrap each route and its children in an error boundary.
 */
export const addErrorBoundaries = (routes: JSX.Element) => visitElements(routes, addErrorBoundary);
